import { expVal } from '@confluence/feature-experiments';
import {
	SIMPLIFY_NAV_NEW_USER,
	ONBOARDING_EXPERIENCES,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { isWithinD0Window, isWithinD0WindowEpoch } from './isWithinD0Window.ts';
import { preloadSimplifiedNavOnboardingStates } from './preloadSimplifiedNavOnboardingStates.ts';
import { preloadSpaceBlogCount } from './preloadSpaceBlogCount.ts';

/**
 * Preloads onboarding states for the simplified nav experiment, and conditionally
 * preloads the space blog count if the user is eligible and still within their D0 period.
 */
export async function preloadSimplifiedNavForSpaceNav(spaceKey: string): Promise<void> {
	const { accessStatus } = getPreloaderFnContext();
	if (
		accessStatus !== AccessStatus.LICENSED_ADMIN_ACCESS &&
		accessStatus !== AccessStatus.LICENSED_USE_ACCESS
	) {
		return;
	}

	const { data, hasErrors } = await preloadSimplifiedNavOnboardingStates();

	if (hasErrors || !data?.onboardingState) {
		return;
	}

	const eligibleEntry = data.onboardingState.find(
		(s) => s.key === SIMPLIFY_NAV_NEW_USER.NTH_USER_D0_SIMPLIFIED_NAV_ELIGIBLE,
	);
	const startAtEntry = data.onboardingState.find(
		(s) => s.key === SIMPLIFY_NAV_NEW_USER.NTH_USER_D0_SIMPLIFIED_NAV_START_AT,
	);

	if (eligibleEntry?.value !== 'true') {
		return;
	}

	// Relevant for real experiment variant users
	if (startAtEntry?.value) {
		if (isWithinD0Window(startAtEntry.value)) {
			await preloadSpaceBlogCount(spaceKey);
		}
		return;
	}

	// TODO: Remove A/A fallback when cc_activation_simplify_nav_new_user_aa is cleaned up.
	// Fallback: use onboarding completed timestamp (epoch ms) for A/A experiment cohort,
	// so the preload can be tested before the real experiment is enabled.
	if (expVal('cc_activation_simplify_nav_new_user_aa', 'cohort', 'control') !== 'control') {
		const completedEntry = data.onboardingState.find(
			(s) => s.key === ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP,
		);

		if (completedEntry?.value) {
			if (isWithinD0WindowEpoch(Number(completedEntry.value))) {
				await preloadSpaceBlogCount(spaceKey);
			}
		}
	}
}
